import { css, cx } from "@emotion/css"
import { useId } from "react"
import { Tooltip } from "react-tooltip"
import "react-tooltip/dist/react-tooltip.css"

import type { NumberFieldOptions } from "./NumberFieldType"
import Spinner from "./Spinner"
import useNumberField from "./useNumberField"

type Props = React.AriaAttributes &
  NumberFieldOptions & {
    error?: boolean
    prefix?: React.ReactNode
    suffix?: React.ReactNode

    className?: string
    placeholder?: string
    readOnly?: boolean
  }

const NumberField = ({
  error,
  prefix,
  suffix,
  className,
  readOnly = false,
  placeholder,
  min,
  max,
  step,
  maxDecimalDigits,
  name,
  value,
  onUpdateValue,
  ...ariaAttributes
}: Props) => {
  const options = { min, max, step, maxDecimalDigits, name, value, onUpdateValue }
  const { onUp, onDown, shouldShowSpinner, errorMessage, shouldShowTooltip, containerProps, numberFieldProps } =
    useNumberField(options, readOnly)
  const inputClassName = cx(styles.default, error && styles.error, className)

  const tooltipId = useId()
  return (
    <span>
      {prefix && <span className={styles.prefix}>{prefix}</span>}
      <span id={tooltipId} {...containerProps} className={styles.inputContainer}>
        <input
          {...ariaAttributes}
          {...numberFieldProps}
          readOnly={readOnly}
          placeholder={placeholder}
          autoComplete="off"
          className={inputClassName}
        />
        {shouldShowSpinner && (
          <span className={styles.spinnerContainer}>
            <Spinner direction="top" onLongPress={onUp} />
            <Spinner direction="bottom" onLongPress={onDown} />
          </span>
        )}
        <Tooltip anchorId={tooltipId} isOpen={shouldShowTooltip} style={{ zIndex: 100 }} place="bottom" variant="error">
          {errorMessage}
        </Tooltip>
      </span>
      {suffix && <span className={styles.suffix}>{suffix}</span>}
    </span>
  )
}

export default NumberField

const styles = {
  default: css`
    display: block;
    width: 65px;
    height: 24px;
    padding-right: ${5 + 15}px;
    font-family: inherit;
    font-size: 12.5px;
    text-align: right;
    border: 1px solid #cfcfcf;
    ::placeholder {
      padding-left: 6px;
      color: #cccccc;
      text-align: left;
    }
    &[readonly] {
      background-color: #cccccc;
    }
  `,
  error: css`
    border: 1px solid #c50d12;
  `,
  prefix: css`
    display: inline-block;
    padding-right: 5px;
  `,
  suffix: css`
    display: inline-block;
    padding-left: 5px;
  `,
  inputContainer: css`
    position: relative;
    display: inline-block;
  `,
  spinnerContainer: css`
    position: absolute;
    top: 0;
    right: 0;
    padding: 2px;
  `,
}
